import { useAuth } from "../../context/AuthContext";
import { formatRangeLabel } from "../../lib/time";
import { useState } from "react";
import type { DateRangeValue } from "../../components/ui/date-range-picker";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import type { AnalyticsExportFormat } from "./analytics-export";

const FORMATS: AnalyticsExportFormat[] = ["CSV", "XLSX", "PDF"];

export function ExportModal({
  range,
  onExport,
  onClose,
}: {
  range: DateRangeValue;
  onExport: (format: AnalyticsExportFormat) => Promise<void>;
  onClose: () => void;
}) {
  const toast = useToast();
  const { user } = useAuth() as any;
  const [format, setFormat] = useState<AnalyticsExportFormat>("PDF");
  const [exporting, setExporting] = useState(false);
  const subtitle = formatRangeLabel(range.start, range.end, user?.timezone);

  const startExport = async () => {
    try {
      setExporting(true);
      await onExport(format);
      onClose();
    } catch (error) {
      toast.danger("Export failed", (error as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title="Export Analytics"
      subtitle={subtitle}
      onClose={onClose}
      width="max-w-lg"
      footer={(
        <>
          <button
            disabled={exporting}
            onClick={onClose}
            className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            disabled={exporting}
            onClick={startExport}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-wait disabled:opacity-60"
          >
            {exporting ? "Preparing..." : "Download Export"}
          </button>
        </>
      )}
    >
      <div>
        <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Format</p>
        <div className="inline-flex rounded-lg border border-line p-1">
          {FORMATS.map((item) => (
            <button
              key={item}
              onClick={() => setFormat(item)}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-semibold transition-colors",
                format === item ? "bg-brand text-white" : "text-ink-2 hover:bg-soft",
              )}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
